import { Box, Center, CenterProps, Flex, FlexProps, useToken } from "@chakra-ui/react";
import { ReactNode } from "react";

import { RanaHeading, SIZES } from "@nens/ranalib";
import { hexToRgb } from "@nens/shared-utils";

type ControlsProps = { children: ReactNode } & FlexProps;

const CONTROLS_PADDING = SIZES[3];

/**
 * Use skipBackground when the control is wrapped in a component
 * that already provides background styling (e.g., ResizableSidebar)
 * to avoid stacking transparent backgrounds
 */
const useControlProps = (fullscreen: boolean, skipBackground = false) => {
  const wrapperProps: CenterProps = {
    pointerEvents: "none",
    position: "absolute",
    zIndex: "docked",
    fontSize: "sm",
  };

  const [grey100] = useToken("colors", ["brand.grey.100"]);
  const grey100RGB = hexToRgb(grey100);

  const DEFAULT_INNER_PROPS: CenterProps = {
    pointerEvents: "all",
    maxH: `calc(100% - ${CONTROLS_PADDING} * 2)`,
    overflow: "auto",
    sx: { "& > *": { flexShrink: 0 } },
    ...(skipBackground
      ? {}
      : {
          bg: `rgba(${grey100RGB}, 0.9)`,
          backdropFilter: "blur(5px)",
        }),
  };

  const innerProps: CenterProps = fullscreen
    ? { ...DEFAULT_INNER_PROPS }
    : { ...DEFAULT_INNER_PROPS, rounded: "base" };

  return { wrapperProps, innerProps };
};

const getRanaMapControls = (fullscreen: boolean) => {
  const controlsPadding = fullscreen ? 0 : CONTROLS_PADDING;

  /**
   * Left-aligned control panel.
   *
   * When used with ResizableSidebar (w="full"), the background is skipped to avoid
   * double-stacking of semi-transparent backgrounds.
   */
  const ControlsLeft = ({ children, ...props }: ControlsProps) => {
    const skipBackground = fullscreen && props.w === "full";
    const { innerProps, wrapperProps } = useControlProps(fullscreen, skipBackground);

    return (
      <Center {...wrapperProps} left={controlsPadding} h="full" w={props.w ?? "auto"}>
        <Flex {...innerProps} flexDirection="column" w="full" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  /**
   * Right-aligned control panel.
   *
   * When used with ResizableSidebar (w="full"), the background is skipped to avoid
   * double-stacking of semi-transparent backgrounds.
   */
  const ControlsRight = ({ children, ...props }: ControlsProps) => {
    const skipBackground = fullscreen && props.w === "full";
    const { innerProps, wrapperProps } = useControlProps(fullscreen, skipBackground);

    return (
      <Center {...wrapperProps} right={controlsPadding} h="full" w={props.w ?? "auto"}>
        <Flex {...innerProps} flexDirection="column" w="full" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsTop = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center {...wrapperProps} top={controlsPadding} w="full">
        <Flex {...innerProps} flexDirection="row" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsBottom = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center {...wrapperProps} bottom={controlsPadding} w="full">
        <Flex {...innerProps} flexDirection="row" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsTopLeft = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center
        {...wrapperProps}
        top={controlsPadding}
        left={controlsPadding}
        h="full"
        alignItems="start"
      >
        <Flex {...innerProps} flexDirection="column" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsTopRight = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center
        {...wrapperProps}
        top={controlsPadding}
        right={controlsPadding}
        h="full"
        alignItems="start"
      >
        <Flex {...innerProps} flexDirection="column" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsBottomLeft = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center
        {...wrapperProps}
        bottom={controlsPadding}
        left={controlsPadding}
        h="full"
        alignItems="end"
      >
        <Flex {...innerProps} flexDirection="column" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsBottomRight = ({ children, ...props }: ControlsProps) => {
    const { innerProps, wrapperProps } = useControlProps(fullscreen);
    return (
      <Center
        {...wrapperProps}
        bottom={controlsPadding}
        right={controlsPadding}
        h="full"
        alignItems="end"
      >
        <Flex {...innerProps} flexDirection="column" {...props}>
          {children}
        </Flex>
      </Center>
    );
  };

  const ControlsHeader = (props: ControlsProps) => (
    <RanaHeading
      px={4}
      h="button"
      display="flex"
      gap={2}
      alignItems="center"
      borderBottomWidth={0}
      borderColor="brand.sky"
      type="h5"
      {...props}
    />
  );

  const Overlay = ({ children, ...rest }: ControlsProps) => (
    <Center
      alignItems="center"
      h="full"
      w="full"
      position="absolute"
      top={0}
      left={0}
      zIndex={1000}
      {...rest}
    >
      <Box
        h="full"
        w="full"
        position="absolute"
        top={0}
        left={0}
        bg="brand.glacier"
        opacity={0.9}
      />
      <Center position="relative" rounded="base" zIndex={1} textStyle="md">
        {children}
      </Center>
    </Center>
  );

  return {
    ControlsLeft,
    ControlsRight,
    ControlsTop,
    ControlsBottom,
    ControlsTopLeft,
    ControlsTopRight,
    ControlsBottomLeft,
    ControlsBottomRight,
    ControlsHeader,
    Overlay,
  };
};

export const RanaMapControls = getRanaMapControls(false);
export const FullscreenRanaMapControls = getRanaMapControls(true);
